import React, { forwardRef, useImperativeHandle, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import './index.less'
import classNames from 'classnames'

let timer = null

type TCarouselProps = {
  children:React.ReactNode,
  autoplay?:boolean,
  times?:number
}

export default forwardRef(({ children, autoplay = true, times = 3000 }:TCarouselProps, ref) => {
  const [itemWidth, setItemWidth] = useState(0)
  const carouselWrap = useRef<any>(null)
  const count = React.Children.count(children)
  const [curIndex, setCurIndex] = useState(1)
  const animationTiming = useRef(true) // 定时任务动画

  useLayoutEffect(() => {
    const onResize = () => {
      setTimeout(() => {
        // eslint-disable-next-line no-unsafe-optional-chaining
        const { width } = carouselWrap.current?.getBoundingClientRect()
        setItemWidth(width)
      }, 200)
    }
    onResize()
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
    }
  }, [carouselWrap.current])

  const timingTask = () => {
    if (!autoplay) {
      return
    }
    if (curIndex === count + 1) {
      setTimeout(() => {
        animationTiming.current = true
        setCurIndex(1)
      }, 500)
    }
    timer = setTimeout(() => {
      animationTiming.current = true
      const _curIndex = curIndex + 1
      setCurIndex(_curIndex)
      if (_curIndex > count + 1) {
        clearTimeout(timer)
      }
    }, times)
  }

  useEffect(() => {
    timingTask()
    return () => {
      clearInterval(timer)
      animationTiming.current = false
    }
  }, [curIndex, count])

  const wrapStyle = useMemo(() => {
    if (curIndex === 1 && animationTiming.current) {
      return {
        width: count * itemWidth * 2 + itemWidth,
        transform: `translate3d(${-curIndex * itemWidth}px, 0px, 0px)`,
      }
    }
    return {
      width: count * itemWidth * 2 + itemWidth,
      transform: `translate3d(${-curIndex * itemWidth}px, 0px, 0px)`,
      transition: 'transform ease 500ms'
    }
  }, [count, itemWidth, curIndex])

  const lastNode = count > 1 && <div
    className="mn-carousel-item"
    style={{ width: itemWidth }}
    data-index={-1}
  >
    {React.Children.toArray(children)[count - 1]}
  </div>

  useImperativeHandle(ref, () => {
    return {
      goTo,
      next,
      prev
    }
  })

  const goTo = (_curIndex) => {
    setCurIndex(_curIndex)
  }

  const next = () => {
    if (curIndex === count + 1) {
      setCurIndex(1)
    } else {
      setCurIndex(pre => (pre + 1))
    }
  }

  const prev = () => {
    if (curIndex === 1) {
      setCurIndex(count)
    } else {
      setCurIndex(pre => (pre - 1))
    }
  }

  return <div className="mn-carousel" ref={carouselWrap}>
    <div className="mn-carousel-list">
      <div
        className="mn-carousel-item-wrap"
        style={wrapStyle}
        onMouseMove={() => {
          clearTimeout(timer)
        }}
        onMouseLeave={() => {
          timingTask()
        }}
      >
        { lastNode }
        {
          React.Children.map(children, (item, i) => {
            return <div
              className="mn-carousel-item"
              style={{ width: itemWidth }}
              key={i}
              data-index={i}
            >{item}</div>
          })
        }
        {
          React.Children.map(children, (item, i) => {
            return <div
              className="mn-carousel-item"
              style={{ width: itemWidth }}
              key={count + i}
              data-index={count + i}
            >{item}</div>
          })
        }
      </div>
      <div
        className="mn-carousel-list-dots"
        onMouseMove={(e) => {
          e.stopPropagation()
        }}
        onMouseLeave={(e) => {
          e.stopPropagation()
        }}
      >
        {
          React.Children.map(children, (item, i) => {
            return <div
              className={classNames('mn-carousel-list-dot', { active: curIndex === i + 1 })}
              onClick={() => {
                goTo(i + 1)
              }}
              key={i}
            />
          })
        }
      </div>
    </div>
  </div>
})
